import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { supabase, url as supabaseUrl } from '../../core/supabase/supabase.client';
import { ADMIN_PANEL_ROLES, AppUserRole, assignedStaffRoles, isAdminPanelRole } from '../../core/auth/roles';

export interface UserFormModel {
  id?: string;
  isEdit: boolean;
  name: string;
  phone?: string;
  email: string;
  role: AppUserRole | string;
  isActive: boolean;
  storeId?: number;
  password?: string;
  originalRole?: string;
  originalStoreId?: number | null;
  additional_roles?: string[];
  staffRoles?: string[];
  originalStaffRoles?: string[];
}

export interface UsersListResult {
  data: any[];
  total: number;
  totalPages: number;
  activeCount: number;
  roleCounts: Record<string, number>;
}

@Injectable({ providedIn: 'root' })
export class UsersService {
  async eligibleSupportStaff(storeId: number): Promise<{ id: string; name: string; role: string }[]> {
    if (!Number.isSafeInteger(storeId) || storeId <= 0) throw new Error('Order store is required');
    const staff: { id: string; name: string; role: string }[] = [];
    for (let offset = 0; ; offset += 200) {
      const { data, error } = await supabase.from('users').select('id,name,role')
        .eq('is_active', true).is('deleted_at', null)
        .or(`role.eq.superadmin,and(role.eq.store_manager,store_id.eq.${storeId})`)
        .order('name').order('id').range(offset, offset + 199);
      if (error) throw new Error(error.message);
      staff.push(...(data ?? []));
      if (!data || data.length < 200) return staff;
    }
  }
  private async invokeManageUser(body: Record<string, unknown>) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const res = await fetch(`${supabaseUrl}/functions/v1/admin-manage-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: environment.supabase.anonKey,
      },
      body: JSON.stringify(body),
    });

    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(payload?.error || 'Account operation failed');
    }
    return payload;
  }

  async getAllUsers(
    page = 1,
    limit = 10,
    search = '',
    sortBy = 'name',
    order: 'ASC' | 'DESC' = 'ASC',
    storeId?: number,
    role?: string | null
  ): Promise<UsersListResult> {
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    let query = supabase.from('users').select('*', { count: 'exact' });

    if (storeId) {
      query = query.eq('store_id', storeId);
    }

    const staffFilter=role && isAdminPanelRole(role) ? `or(role.eq.${role},additional_roles.cs.{${role}})` : null;
    if(role && !staffFilter)query=query.eq('role',role);

    if (search) {
      const fields=`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`;
      query=query.or(staffFilter?`and(${staffFilter},or(${fields}))`:fields);
    }else if(staffFilter)query=query.or(staffFilter.slice(3,-1));

    const safeSort = ['name', 'email', 'role', 'created_at', 'phone'].includes(
      sortBy
    )
      ? sortBy
      : 'name';

    query = query.order(safeSort, { ascending: order === 'ASC' });

    const { data, count, error } = await query.range(from, to);
    if (error) throw error;

    const [{ count: activeCount }, roleBuckets] = await Promise.all([
      supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true),
      this.getRoleCounts(),
    ]);

    return {
      data: data ?? [],
      total: count ?? 0,
      totalPages: Math.ceil((count ?? 0) / limit),
      activeCount: activeCount ?? 0,
      roleCounts: roleBuckets,
    };
  }

  private async getRoleCounts(): Promise<Record<string, number>> {
    const roles = [...ADMIN_PANEL_ROLES, 'rider', 'user'] as const;
    const entries = await Promise.all(
      roles.map(async (r) => {
        const { count } = await supabase
          .from('users')
          .select('*', { count: 'exact', head: true })
          .eq('role', r);
        return [r, count ?? 0] as const;
      })
    );
    return Object.fromEntries(entries);
  }

  async createUser(user: UserFormModel) {
    return this.invokeManageUser({
      action: 'create',
      name: user.name,
      role: user.role,
      email: user.email || undefined,
      phone: user.phone || undefined,
      password: user.password || undefined,
      storeId: user.storeId ?? null,
      isActive: user.isActive,
    });
  }

  async setUserActive(userId: string, isActive: boolean) {
    return this.invokeManageUser({
      action: 'set_active',
      userId,
      isActive,
    });
  }

  async updateUser(user: UserFormModel) {
    if (!user.id) throw new Error('User ID is required');

    const roles=user.staffRoles ?? assignedStaffRoles(user.role,user.additional_roles);
    const original=user.originalStaffRoles ?? assignedStaffRoles(user.originalRole);
    if (isAdminPanelRole(user.role) && (JSON.stringify([...roles].sort()) !== JSON.stringify([...original].sort()) ||
      (user.storeId ?? null) !== (user.originalStoreId ?? null))) {
      const { error } = await supabase.rpc('admin_set_staff_roles', {
        p_user: user.id, p_roles: roles, p_store: roles.includes('superadmin') ? null : user.storeId ?? null,
        p_expected_roles: original, p_expected_store: user.originalStoreId ?? null,
      });
      if (error) throw error;
    }

    const { error } = await supabase
      .from('users')
      .update({
        name: user.name,
        phone: user.phone || null,
      })
      .eq('id', user.id);

    if (error) throw error;
  }

  async updateProfile(userId: string, updates: Partial<UserFormModel>) {
    const { error } = await supabase
      .from('users')
      .update({
        name: updates.name,
        phone: updates.phone,
      })
      .eq('id', userId);

    if (error) throw error;
  }
}
